import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pencil, Save, FileDown, Loader2, Trash2, Info, ListChecks, ScrollText, ExternalLink,
  Maximize2, Minimize2, ZoomIn, ZoomOut, Scan, MessageCircle, BadgeCheck, Ban, Wallet, Paperclip, X, Printer,
} from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api, { API, fmtIDR, fmtDate, printPdf } from "@/lib_app/api";
import { toast } from "sonner";
import DateInput from "@/components/app/DateInput";
import NumberInput from "@/components/app/NumberInput";
import InvoiceDocs from "@/components/app/InvoiceDocs";
import InvoicePaymentsPanel from "@/components/app/InvoicePaymentsPanel";
import { Switch } from "@/components/ui/switch";
import Avatar from "@/components/app/Avatar";
import { useAuth } from "@/context/AuthContext";
import {
  ItemsTab, TermsTab, InfoRow, Field, inp, num, computeTotals, terbilang,
} from "@/components/app/QuotationDetailModal";

export const INVOICE_STATUS = {
  draft: { label: "Draft", chip: "bg-[#F4F5F7] text-[#626061] border-[#DCE0E7]" },
  issued: { label: "Terbit", chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]" },
  partial: { label: "Dibayar Sebagian", chip: "bg-[#FFF3DA] text-[#8C6318] border-[#F2D9A0]" },
  paid: { label: "Lunas", chip: "bg-[#E4F9EC] text-[#3f7a52] border-[#B7E7C7]" },
  void: { label: "Dibatalkan", chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]" },
};

/** Project names are often prefixed with the client name (e.g. "BXSea - Revisi Konten").
 * Strip that prefix for display when the customer name is already shown alongside it. */
export const shortProjectName = (projectName, customer) => {
  const name = (projectName || "").trim();
  const cust = (customer || "").trim();
  if (!name || !cust) return name;
  if (name.toLowerCase().startsWith(cust.toLowerCase())) {
    const rest = name.slice(cust.length).replace(/^[\s\-–—:]+/, "").trim();
    return rest;
  }
  return name;
};

const termsToText = (groups) => (groups || [])
  .map((g, i) => [`${i + 1}. ${g.title}`, ...(g.items || []).map((it) => `   - ${it}`)].join("\n"))
  .join("\n");

const emptyGroup = () => ({ title: "", price_mode: "lump_sum", qty: 1, unit: "ls", price: 0, items: [{ description: "" }] });

function toForm(d) {
  return {
    id: d.id, number: d.number || "", status: d.status || "draft",
    project_id: d.project_id, project_name: d.project_name, prospect_id: d.prospect_id,
    client_id: d.client_id, quotation_id: d.quotation_id, quotation_number: d.quotation_number,
    sales_id: d.sales_id, customer: d.customer || "", customer_address: d.customer_address || "",
    pic_name: d.pic_name || "", pic_contact: d.pic_contact || "",
    date: d.date || new Date().toISOString().slice(0, 10), due_date: d.due_date || "",
    ppn_type: d.ppn_type || "ppn", terms: d.terms || "", notes: d.notes || "",
    bank_account_id: d.bank_account_id || d.bank_account?.id || "",
    signer_id: d.signer_id || d.signer?.id || "",
    termin_enabled: !!d.termin_enabled,
    termin_label: d.termin_label || "",
    termin_percent: d.termin_percent ?? 100,
    termin_base: d.termin_base ?? 0,
    terms_groups: (d.terms_groups || []).map((g) => ({ title: g.title || "", items: [...(g.items || [])] })),
    groups: (d.groups && d.groups.length ? d.groups : [emptyGroup()]).map((g) => ({
      title: g.title || "", price_mode: g.price_mode === "itemized" ? "itemized" : "lump_sum",
      qty: g.qty ?? 1, unit: g.unit || "ls", price: g.price ?? 0,
      items: (g.items && g.items.length ? g.items : [{ description: "" }]).map((it) => ({
        description: it.description || "", qty: it.qty ?? 1, unit: it.unit || "ls", price: it.price ?? 0,
        children: [...(it.children || [])],
      })),
    })),
  };
}

export default function InvoiceDetailModal({ open, invoiceId, prefill, initialMode = "read", onClose, onSaved, onDeleted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [team, setTeam] = useState([]);
  const [previewFull, setPreviewFull] = useState(false);
  const [banks, setBanks] = useState([]);
  const [signers, setSigners] = useState([]);
  const [termTemplates, setTermTemplates] = useState([]);
  const [confirm, setConfirm] = useState(null); // "delete" | "issue" | "void"
  const [ar, setAr] = useState({ paid_amount: 0, payments: [] });
  const [docs, setDocs] = useState([]);

  const refreshAr = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/invoices/${id}/payments`); setAr(r.data || { paid_amount: 0, payments: [] }); } catch { setAr({ paid_amount: 0, payments: [] }); }
  };
  const refreshDocs = async (id) => {
    if (!id) return;
    try { const r = await api.get(`/invoices/${id}/attachments`); setDocs(r.data || []); } catch { setDocs([]); }
  };

  const loadTermTemplates = () => api.get("/quotation-term-templates").then((r) => setTermTemplates(r.data || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    api.get("/settings/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/settings/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => {});
    api.get("/settings/invoice-signers").then((r) => setSigners(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setTeam(r.data || [])).catch(() => {});
    loadTermTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setTab("info");
    if (invoiceId) {
      setLoading(true);
      api.get(`/invoices/${invoiceId}`)
        .then((r) => { setData(r.data); setForm(toForm(r.data)); setMode(initialMode); })
        .catch(() => toast.error("Gagal memuat invoice"))
        .finally(() => setLoading(false));
      refreshAr(invoiceId);
      refreshDocs(invoiceId);
    } else {
      setData(null);
      setAr({ paid_amount: 0, payments: [] });
      setDocs([]);
      setForm(prefill ? toForm({ ...prefill, id: null, number: "" }) : null);
      setMode("edit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceId, prefill]);

  // Ganti jenis invoice: rekening default ikut pindah & nomor mengambil urutan terbaru seri tujuan.
  const switchPpnType = (v) => {
    const def = banks.find((b) => (v === "non_ppn" ? b.is_default_non_ppn : b.is_default_ppn));
    const current = form?.number || "";
    const optimistic = renumber(current, form?.ppn_type, v);
    setForm((f) => ({ ...f, ppn_type: v, bank_account_id: def?.id || "", number: optimistic }));
    if (optimistic === current) return; // nomor custom / kosong -> jangan diutak-atik
    if (v === data?.ppn_type && data?.number) {
      setField("number", data.number); // kembali ke jenis semula -> pakai nomor asli invoice
      return;
    }
    api.get("/invoices/next-number", { params: { ppn_type: v, date: form?.date || undefined } })
      .then((r) => setForm((f) => (f.ppn_type === v ? { ...f, number: r.data.number } : f)))
      .catch(() => {});
  };

  const prefixFor = (t) => (t === "ppn"
    ? (company?.invoice_prefix || "INV-IDS")
    : (company?.invoice_prefix_non_ppn || "P-INV-IDS"));
  // Ganti seri nomor saat jenis invoice ditukar (samakan dgn renumber di backend).
  const renumber = (number, from, to) => {
    const parts = String(number || "").split("/");
    if (parts.length < 2 || parts[1] !== prefixFor(from)) return number;
    parts[1] = prefixFor(to);
    return parts.join("/");
  };

  const selectedBank = banks.find((b) => b.id === form?.bank_account_id)
    || banks.find((b) => (form?.ppn_type === "non_ppn" ? b.is_default_non_ppn : b.is_default_ppn))
    || null;
  const selectedSigner = signers.find((sg) => sg.id === form?.signer_id)
    || signers.find((sg) => sg.is_default) || null;

  const totals = useMemo(() => {
    if (!form) return { subtotal: 0, ppn: 0, grand: 0, billed: 0, gsubs: [] };
    const base = computeTotals(form.groups, form.ppn_type);
    const billed = form.termin_enabled
      ? Math.round(num(form.termin_base) * num(form.termin_percent)) / 100
      : base.subtotal;
    const ppn = form.ppn_type === "ppn" ? Math.round(billed * 11) / 100 : 0;
    return { ...base, billed, ppn, grand: billed + ppn };
  }, [form]);

  const billing = (data?.billing || prefill?.billing || null);
  const billedElsewhere = billing?.billed_amount || 0;
  const projectBase = billing?.project_base_value || 0;
  const totalAfterThis = billedElsewhere + (form?.status === "void" ? 0 : (form?.termin_enabled ? totals.billed : totals.subtotal));
  const overBilled = projectBase > 0 && totalAfterThis > projectBase + 1;

  if (!open) return null;

  const isOwner = user?.role === "owner";
  const isFinance = user?.role === "finance";
  const isDirector = user?.role === "sales_director";
  const canIssue = isOwner || isFinance;
  const status = form?.status || "draft";
  const canEdit = (isOwner || isFinance || isDirector || (user && (!data || data.sales_id === user.user_id)))
    && status !== "void" && (status === "draft" || canIssue);
  const salesUser = team.find((u) => u.user_id === form?.sales_id);
  const salesName = salesUser?.name || data?.sales_name || "";
  const salesNickname = salesUser?.nickname || salesName;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setGroup = (gi, patch) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setItem = (gi, ii, patch) => setForm((f) => ({
    ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : g)),
  }));
  const addGroup = () => setForm((f) => ({ ...f, groups: [...f.groups, emptyGroup()] }));
  const removeGroup = (gi) => setForm((f) => ({ ...f, groups: f.groups.filter((_, i) => i !== gi) }));
  const addItem = (gi) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: [...g.items, { description: "", qty: 1, unit: "ls", price: 0, children: [] }] } : g)) }));
  const removeItem = (gi, ii) => setForm((f) => ({ ...f, groups: f.groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const setPriceMode = (gi, m) => setForm((f) => ({
    ...f,
    groups: f.groups.map((g, i) => {
      if (i !== gi) return g;
      if (m === "itemized") {
        return {
          ...g, price_mode: m,
          items: g.items.map((it, idx) => ({
            description: it.description || "",
            children: [...(it.children || [])],
            qty: num(it.qty) || (idx === 0 ? (num(g.qty) || 1) : 1),
            unit: it.unit || g.unit || "ls",
            price: num(it.price) || (idx === 0 ? num(g.price) : 0),
          })),
        };
      }
      const sub = g.items.reduce((acc, it) => acc + num(it.qty) * num(it.price), 0);
      return {
        ...g, price_mode: m, qty: g.qty || 1, unit: g.unit || "ls", price: sub || g.price || 0,
        items: g.items.map((it) => ({ description: it.description || "", children: [...(it.children || [])] })),
      };
    }),
  }));

  const setTermGroup = (gi, patch) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setTermItem = (gi, ii, val) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.map((t, j) => (j === ii ? val : t)) } : g)) }));
  const addTermGroup = () => setForm((f) => ({ ...f, terms_groups: [...(f.terms_groups || []), { title: "", items: [] }] }));
  const removeTermGroup = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.filter((_, i) => i !== gi) }));
  const addTermItem = (gi) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: [...(g.items || []), ""] } : g)) }));
  const removeTermItem = (gi, ii) => setForm((f) => ({ ...f, terms_groups: f.terms_groups.map((g, i) => (i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g)) }));
  const moveGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.groups];
    const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, groups: arr };
  });
  const moveItem = (gi, ii, dir) => setForm((f) => ({
    ...f,
    groups: f.groups.map((g, i) => {
      if (i !== gi) return g;
      const arr = [...g.items];
      const to = ii + dir;
      if (to < 0 || to >= arr.length) return g;
      [arr[ii], arr[to]] = [arr[to], arr[ii]];
      return { ...g, items: arr };
    }),
  }));

  const moveTermGroup = (gi, dir) => setForm((f) => {
    const arr = [...f.terms_groups];
    const to = gi + dir;
    if (to < 0 || to >= arr.length) return f;
    [arr[gi], arr[to]] = [arr[to], arr[gi]];
    return { ...f, terms_groups: arr };
  });

  const applyTermTemplate = (tplId) => {
    const tpl = termTemplates.find((t) => t.id === tplId);
    if (!tpl) return;
    setForm((f) => ({ ...f, terms_groups: (tpl.groups || []).map((g) => ({ title: g.title || "", items: [...(g.items || [])] })) }));
    toast.success(`Template "${tpl.name}" diterapkan`);
  };

  const saveTermTemplate = async (name) => {
    const groups = (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim()));
    if (!groups.length) { toast.error("Syarat & ketentuan masih kosong"); return; }
    try {
      await api.post("/quotation-term-templates", { name: name.trim(), groups });
      await loadTermTemplates();
      toast.success("Template disimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal menyimpan template");
    }
  };

  const deleteTermTemplate = async (tplId) => {
    try {
      await api.delete(`/quotation-term-templates/${tplId}`);
      await loadTermTemplates();
      toast.success("Template dihapus");
    } catch { toast.error("Gagal menghapus template"); }
  };

  const cancelEdit = () => {
    if (!data) { onClose(); return; }
    setForm(toForm(data));
    setMode("read");
  };

  const save = async () => {
    if (!form.customer.trim()) { toast.error("Nama pelanggan wajib diisi"); return; }
    if (!form.project_id) { toast.error("Invoice harus terhubung ke proyek deal"); return; }
    setSaving(true);
    try {
      const payload = {
        project_id: form.project_id, quotation_id: form.quotation_id || null,
        number: (form.number || "").trim() || null,
        customer: form.customer, customer_address: form.customer_address, pic_name: form.pic_name, pic_contact: form.pic_contact,
        date: form.date, due_date: form.due_date || null, ppn_type: form.ppn_type,
        bank_account_id: form.bank_account_id || null,
        signer_id: form.signer_id || null,
        termin_enabled: !!form.termin_enabled,
        termin_label: form.termin_label || "",
        termin_percent: num(form.termin_percent),
        termin_base: num(form.termin_base),
        terms: termsToText(form.terms_groups),
        terms_groups: (form.terms_groups || []).filter((g) => (g.title || "").trim() || (g.items || []).some((t) => (t || "").trim())),
        notes: form.notes,
        groups: form.groups.map((g) => ({
          title: g.title, price_mode: g.price_mode, qty: num(g.qty), unit: g.unit, price: num(g.price),
          items: g.items.map((it) => ({
            description: it.description, qty: num(it.qty), unit: it.unit, price: num(it.price),
            children: (it.children || []).map((c) => String(c || "").trim()).filter(Boolean),
          })),
        })),
      };
      const res = form.id
        ? await api.patch(`/invoices/${form.id}`, payload)
        : await api.post("/invoices", payload);
      toast.success(form.id ? "Invoice diperbarui" : `Invoice ${res.data.number} dibuat`);
      setData(res.data);
      setForm(toForm(res.data));
      setMode("read");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const runAction = async (action) => {
    setConfirm(null);
    try {
      const r = await api.post(`/invoices/${data.id}/${action}`);
      setData(r.data);
      setForm(toForm(r.data));
      toast.success(action === "issue" ? `Invoice ${r.data.number} diterbitkan` : "Invoice dibatalkan");
      onSaved?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal memproses");
    }
  };

  const remove = async () => {
    setConfirm(null);
    try {
      await api.delete(`/invoices/${data.id}`);
      toast.success("Invoice dihapus");
      onDeleted?.(data.id);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal menghapus"); }
  };

  const downloadPdf = () => {
    if (!data?.id) return;
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/invoices/${data.id}/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const printInvoicePdf = async () => {
    if (!data?.id) return;
    try { await printPdf(`/invoices/${data.id}/pdf`); } catch { toast.error("Gagal mencetak PDF"); }
  };

  const sendWhatsApp = () => {
    if (!data?.id) return;
    const raw = form.pic_contact || "";
    const digits = raw.replace(/[^0-9]/g, "");
    if (!digits || digits.length < 9 || raw.includes("@")) {
      toast.error("Nomor WhatsApp PIC pelanggan belum valid — isi Kontak PIC dengan nomor telepon");
      return;
    }
    const phone = digits.startsWith("0") ? "62" + digits.slice(1) : digits.startsWith("62") ? digits : "62" + digits;
    const lines = [
      `Halo ${form.pic_name || form.customer},`,
      `Berikut Invoice ${form.number || ""} untuk ${form.customer} senilai ${fmtIDR(totals.grand)}${form.termin_enabled ? ` (${form.termin_label || "Termin"} ${num(form.termin_percent)}%)` : ""}.`,
      form.due_date ? `Jatuh tempo pembayaran: ${fmtDate(form.due_date)}.` : null,
      data?.public_pdf_url ? `Dokumen lengkap: ${data.public_pdf_url}` : null,
      null,
      `Terima kasih,`,
      `${salesName}${company?.name ? " · " + company.name : ""}`,
    ].filter((l) => l !== null);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  const addArPayment = async (pay, file) => {
    try {
      const created = await api.post(`/invoices/${data.id}/payments`, pay);
      if (file && created.data?.id && form.project_id) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/projects/${form.project_id}/payments/${created.data.id}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch { toast.error("Pembayaran tersimpan tapi bukti gagal diupload"); }
      }
      await refreshAr(data.id);
      const r = await api.get(`/invoices/${data.id}`);
      setData(r.data); setForm(toForm(r.data));
      toast.success("Pembayaran dicatat");
      onSaved?.(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal mencatat pembayaran"); }
  };

  const deleteArPayment = async (p) => {
    if (!window.confirm(`Hapus pembayaran ${fmtIDR(p.amount)}?`)) return;
    try {
      await api.delete(`/invoices/${data.id}/payments/${p.id}`);
      await refreshAr(data.id);
      const r = await api.get(`/invoices/${data.id}`);
      setData(r.data); setForm(toForm(r.data));
      toast.success("Pembayaran dihapus");
      onSaved?.(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menghapus pembayaran"); }
  };

  const openPaymentProof = (p) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/projects/${form.project_id}/payments/${p.id}/attachment/download?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const uploadDocs = async (invId, category, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    fd.append("category", category);
    try {
      await api.post(`/invoices/${invId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshDocs(invId);
      toast.success("Dokumen diupload");
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal upload dokumen"); }
  };

  const deleteDoc = async (att) => {
    if (!window.confirm(`Hapus dokumen "${att.filename}"?`)) return;
    try {
      await api.delete(`/invoices/${data.id}/attachments/${att.id}`);
      await refreshDocs(data.id);
      toast.success("Dokumen dihapus");
    } catch { toast.error("Gagal menghapus dokumen"); }
  };

  const openDoc = (att) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/invoices/${att.invoice_id}/attachments/${att.id}/download?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const tabs = [
    { key: "info", label: "Informasi", icon: Info },
    { key: "items", label: "Item & Harga", icon: ListChecks },
    { key: "terms", label: "Syarat & Ketentuan", icon: ScrollText },
    ...(form?.id ? [
      { key: "pembayaran", label: "Pembayaran", icon: Wallet },
      { key: "lampiran", label: "Lampiran", icon: Paperclip },
    ] : []),
  ];
  const st = INVOICE_STATUS[status] || INVOICE_STATUS.draft;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-[1560px] w-[96vw] max-h-[92vh] overflow-hidden rounded-3xl border-0 p-0"
        data-testid="invoice-detail-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Invoice {data?.number || "Baru"}</DialogTitle>
        <DialogDescription className="sr-only">Detail invoice</DialogDescription>

        {loading || !form ? (
          <div className="h-[60vh] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B8FE2]" />
          </div>
        ) : (
          <div className="flex h-[92vh] max-h-[92vh]">
            <div className={`${previewFull ? "hidden" : "block"} w-full lg:w-1/2 lg:shrink-0 min-w-0 overflow-y-auto p-6 pb-16 space-y-5`}>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold" data-testid="invoice-modal-label">
                {form.id ? "Detail Invoice" : "Buat Invoice"}{mode === "edit" ? " · Mode Edit" : ""}
              </p>

              {/* HERO */}
              <div
                className="rounded-2xl p-5 backdrop-blur-xl"
                style={{
                  background: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))",
                  border: "1px solid rgba(150,175,220,0.45)",
                  boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 font-mono" data-testid="invoice-modal-number">
                        {form.number || "Nomor otomatis saat disimpan"}
                      </span>
                      <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${st.chip}`} data-testid="invoice-modal-status">
                        {st.label}
                      </span>
                      <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${form.ppn_type === "ppn" ? "bg-[#D8EEDF] text-[#3f7a52]" : "bg-[#DCE0E7] text-[#626061]"}`}>
                        {form.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-[#090808] leading-tight truncate" data-testid="invoice-modal-customer">{form.customer || "Pelanggan Baru"}</h2>
                    {shortProjectName(form.project_name, form.customer) && (
                      <p className="text-xs font-semibold text-[#3B4A63] mt-0.5 truncate" data-testid="invoice-modal-project">{shortProjectName(form.project_name, form.customer)}</p>
                    )}
                  </div>
                  {form.project_id && (
                    <button
                      onClick={() => { onClose(); navigate(`/projects?open=${form.project_id}`); }}
                      data-testid="invoice-open-project"
                      className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 shrink-0 shadow-sm gap-1.5 hover:bg-white"
                    >
                      <ExternalLink className="w-3 h-3" /> Proyek
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Avatar name={salesName} picture={salesUser?.picture} size="sm" className="!w-7 !h-7 !text-[11px]" />
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Sales</div>
                      <div className="text-sm font-semibold text-[#090808]" data-testid="invoice-hero-sales">{salesNickname || "-"}</div>
                    </div>
                  </div>
                  <div className="w-px h-8 bg-white/70 hidden sm:block" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tanggal</div>
                    <div className="text-sm font-semibold text-[#090808] font-num tabular-nums">{form.date ? fmtDate(form.date) : "-"}</div>
                  </div>
                  <div className="w-px h-8 bg-white/70 hidden sm:block" />
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Jatuh Tempo</div>
                    <div className="text-sm font-semibold text-[#090808] font-num tabular-nums" data-testid="invoice-hero-due">{form.due_date ? fmtDate(form.due_date) : "—"}</div>
                  </div>
                  {form.id && canIssue && status === "draft" && (
                    <>
                      <div className="flex-1" />
                      <button
                        onClick={() => setConfirm("issue")}
                        data-testid="invoice-issue-btn"
                        className="h-9 rounded-full gap-1.5 px-4 font-semibold shadow-sm text-xs bg-gradient-to-r from-[#57D28A] to-[#3EB876] hover:from-[#3EB876] hover:to-[#2FA365] text-white inline-flex items-center"
                      >
                        <BadgeCheck className="w-3.5 h-3.5" /> Terbitkan
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* STAT STRIP */}
              <div className="grid grid-cols-3 rounded-2xl bg-white/70 border border-white/80 backdrop-blur-xl overflow-hidden" style={{ boxShadow: "0 6px 18px rgba(59,74,99,0.08)" }} data-testid="invoice-totals-strip">
                <div className="p-4 border-r border-[#DCE0E7]/60">
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nilai Item</div>
                  <div className="text-lg font-bold text-[#090808] font-num tabular-nums mt-1">{fmtIDR(totals.subtotal)}</div>
                </div>
                {form.termin_enabled ? (
                  <div className="p-4 border-r border-[#DCE0E7]/60">
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold truncate">
                      {(form.termin_label || "Termin") + " " + num(form.termin_percent) + "%"}
                    </div>
                    <div className="text-lg font-bold text-[#3B4A63] font-num tabular-nums mt-1" data-testid="invoice-termin-amount">{fmtIDR(totals.billed)}</div>
                    <div className="text-[10px] font-semibold text-[#A0A3A8] mt-1 truncate" data-testid="invoice-ppn-sub">
                      {form.ppn_type === "ppn"
                        ? <>PPN 11% <span className="text-[#626061] font-num tabular-nums">+{fmtIDR(totals.ppn)}</span></>
                        : "Non-PPN"}
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-r border-[#DCE0E7]/60">
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">{form.ppn_type === "ppn" ? "PPN 11%" : "PPN"}</div>
                    <div className="text-lg font-bold font-num tabular-nums mt-1 text-[#626061]" data-testid="invoice-ppn-amount">{form.ppn_type === "ppn" ? fmtIDR(totals.ppn) : "Non-PPN"}</div>
                  </div>
                )}
                <div className="p-4">
                  <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Total Tagihan</div>
                  <div className="text-lg font-bold text-[#3B4A63] font-num tabular-nums mt-1" data-testid="invoice-grand-total">{fmtIDR(totals.grand)}</div>
                </div>
              </div>

              {/* TAB BAR */}
              <div className="flex items-center justify-between border-b border-[#DCE0E7] gap-3">
                <div className="flex items-center flex-wrap flex-1">
                  {tabs.map((t) => {
                    const Ico = t.icon;
                    const active = tab === t.key;
                    return (
                      <button
                        key={t.key} onClick={() => setTab(t.key)} data-testid={`invoice-tab-${t.key}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-3 text-[13px] transition-colors whitespace-nowrap border-b-2 ${
                          active ? (mode === "edit" ? "text-[#3B4A63] border-[#3B4A63] font-bold" : "text-[#090808] border-[#090808] font-bold") : "text-[#A0A3A8] border-transparent hover:text-[#626061]"
                        }`}
                      >
                        <Ico className="w-3.5 h-3.5" /> {t.label}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pb-2">
                  {mode === "read" && canEdit && (
                    <IconAction icon={Pencil} label="Edit" variant="info" size="sm" onClick={() => setMode("edit")} testid="invoice-edit-toggle" />
                  )}
                  {mode === "edit" && (
                    <>
                      <IconAction icon={X} label="Batal" size="sm" onClick={cancelEdit} disabled={saving} testid="invoice-edit-cancel" />
                      <IconAction icon={Save} label={saving ? "Menyimpan…" : "Simpan"} variant="primary" size="sm" onClick={save} disabled={saving} testid="invoice-save-btn" />
                    </>
                  )}
                </div>
              </div>

              {/* TAB CONTENT */}
              {tab === "info" && (
                mode === "read" ? (
                  <div className="space-y-0 rounded-2xl bg-white/50 border border-[#E5EEFA]/60 px-5 pt-2 pb-4">
                    <InfoRow label="No. Invoice" value={form.number || "-"} valueClass="font-num" />
                    <InfoRow label="Pelanggan" value={form.customer || "-"} />
                    <InfoRow label="Alamat Pelanggan" value={form.customer_address || "-"} />
                    <InfoRow label="Proyek" value={form.project_name || "-"} />
                    <InfoRow label="Ref. Quotation" value={form.quotation_number || "-"} valueClass="font-num" />
                    <InfoRow label="Sales" value={salesName || "-"} />
                    <InfoRow label="Kontak" value={form.pic_contact || "-"} />
                    <InfoRow label="Tanggal" value={form.date ? fmtDate(form.date) : "-"} valueClass="font-num" />
                    <InfoRow label="Jatuh Tempo" value={form.due_date ? fmtDate(form.due_date) : "-"} valueClass="font-num" />
                    <InfoRow label="Pajak" value={form.ppn_type === "ppn" ? "PPN 11% (rekening PT)" : "Non-PPN (rekening pribadi)"} />
                    <InfoRow label="Rekening Tujuan" value={selectedBank ? `${selectedBank.bank_name} · ${selectedBank.account_no} (a.n. ${selectedBank.holder})` : "-"} />
                    <InfoRow label="Penandatangan" value={selectedSigner ? `${selectedSigner.name}${selectedSigner.title ? ` · ${selectedSigner.title}` : ""}` : "-"} />
                    {form.termin_enabled && (
                      <>
                        <InfoRow label="Termin" value={`${form.termin_label || "Termin"} · ${num(form.termin_percent)}%`} />
                        <InfoRow label="Nilai Dasar Termin" value={fmtIDR(form.termin_base)} valueClass="font-num" />
                        <InfoRow label="Nilai Ditagih" value={fmtIDR(totals.billed)} valueClass="font-num" />
                      </>
                    )}
                    {form.id && status !== "draft" && (
                      <>
                        <InfoRow label="Terbayar" value={fmtIDR(ar.paid_amount || 0)} valueClass="font-num" />
                        <InfoRow label="Sisa Tagihan" value={fmtIDR(Math.max(0, (data?.grand_total ?? totals.grand) - (ar.paid_amount || 0)))} valueClass="font-num" />
                      </>
                    )}
                    {billing && projectBase > 0 && (
                      <div className="pt-4">
                        <BillingProgress
                          projectBase={projectBase}
                          billedElsewhere={billedElsewhere}
                          thisAmount={form.status === "void" ? 0 : (form.termin_enabled ? totals.billed : totals.subtotal)}
                          overBilled={overBilled}
                        />
                      </div>
                    )}
                    <div className="pt-4">
                      <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mb-2">Catatan Internal</div>
                      {form.notes ? (
                        <div className="rounded-xl bg-white/70 border border-[#DCE0E7]/70 p-3 text-sm text-[#3B4A63] whitespace-pre-wrap">{form.notes}</div>
                      ) : (
                        <div className="rounded-xl bg-[#F9FAFC] border border-[#DCE0E7]/70 p-3 text-sm text-[#A0A3A8] italic">Belum ada catatan.</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Pelanggan (Billed To)" required>
                        <input data-testid="inv-customer" value={form.customer} onChange={(e) => setField("customer", e.target.value)} className={inp} placeholder="Nama pelanggan / instansi" />
                      </Field>
                      <Field label="Kontak">
                        <input data-testid="inv-contact" value={form.pic_contact} onChange={(e) => setField("pic_contact", e.target.value)} className={inp} placeholder="No. telepon / email" />
                      </Field>
                      <div className="md:col-span-2">
                        <Field label="Alamat Pelanggan (Billed To)">
                          <Textarea
                            data-testid="inv-customer-address"
                            value={form.customer_address}
                            onChange={(e) => setField("customer_address", e.target.value)}
                            rows={2}
                            className="rounded-xl bg-white"
                            placeholder="Alamat perusahaan / instansi pelanggan"
                          />
                        </Field>
                      </div>
                      <Field label="No. Invoice (kosongkan = otomatis)">
                        <input data-testid="inv-number" value={form.number} onChange={(e) => setField("number", e.target.value)} className={inp + " font-num"} placeholder="001/INV-IDS/I/2026" />
                      </Field>
                      <Field label="Jenis Invoice">
                        <div className="flex items-center gap-2 h-10">
                          {[["ppn", "PPN 11%"], ["non_ppn", "Non-PPN"]].map(([v, l]) => (
                            <button key={v} type="button" data-testid={`inv-ppn-${v}`} onClick={() => switchPpnType(v)}
                              className={`h-8 px-3 rounded-full text-xs font-semibold border transition-all ${form.ppn_type === v ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-black/10 hover:border-[#3B4A63]"}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                        <span className="text-[10px] text-[#A0A3A8] mt-1 block">
                          {form.ppn_type === "ppn" ? "Nomor seri INV-IDS · transfer ke rekening PT" : "Nomor seri P-INV-IDS · transfer ke rekening pribadi"}
                        </span>
                      </Field>
                      <Field label="Tanggal Invoice">
                        <DateInput data-testid="inv-date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="h-10 rounded-lg bg-white" />
                      </Field>
                      <Field label="Jatuh Tempo">
                        <DateInput data-testid="inv-due-date" value={form.due_date} onChange={(e) => setField("due_date", e.target.value)} className="h-10 rounded-lg bg-white" />
                      </Field>
                      <Field label="Rekening Tujuan (tampil di PDF)">
                        <Select value={form.bank_account_id || selectedBank?.id || ""} onValueChange={(v) => setField("bank_account_id", v)}>
                          <SelectTrigger data-testid="inv-bank-select" className="h-10 rounded-lg bg-white border-[#DCE0E7] text-sm">
                            <SelectValue placeholder={banks.length ? "Pilih rekening" : "Belum ada rekening"} />
                          </SelectTrigger>
                          <SelectContent>
                            {banks.map((b) => (
                              <SelectItem key={b.id} value={b.id} className="text-sm" data-testid={`inv-bank-option-${b.id}`}>
                                <span className="font-semibold">{b.bank_name}</span>
                                <span className="text-[#A0A3A8]"> · {b.account_no} · {b.kind === "pt" ? "PT" : "Pribadi"}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedBank && (
                          <span className="text-[10px] text-[#A0A3A8] mt-1 block">a.n. {selectedBank.holder}</span>
                        )}
                      </Field>
                      <Field label="Penandatangan">
                        <Select value={form.signer_id || selectedSigner?.id || ""} onValueChange={(v) => setField("signer_id", v)}>
                          <SelectTrigger data-testid="inv-signer-select" className="h-10 rounded-lg bg-white border-[#DCE0E7] text-sm">
                            <SelectValue placeholder={signers.length ? "Pilih penandatangan" : "Belum ada penandatangan"} />
                          </SelectTrigger>
                          <SelectContent>
                            {signers.map((sg) => (
                              <SelectItem key={sg.id} value={sg.id} className="text-sm" data-testid={`inv-signer-option-${sg.id}`}>
                                <span className="font-semibold">{sg.name}</span>
                                <span className="text-[#A0A3A8]"> · {sg.title || "-"}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="md:col-span-2">
                        <TerminEditor
                          form={form} setField={setField} totals={totals}
                          billing={billing} billedElsewhere={billedElsewhere}
                          projectBase={projectBase} totalAfterThis={totalAfterThis} overBilled={overBilled}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Field label="Catatan Internal (tidak muncul di PDF)">
                          <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="rounded-xl bg-white" data-testid="inv-notes" />
                        </Field>
                      </div>
                    </div>
                  </div>
                )
              )}

              {tab === "items" && (
                <ItemsTab
                  form={form} mode={mode} totals={totals}
                  setGroup={setGroup} setItem={setItem} addGroup={addGroup} removeGroup={removeGroup}
                  addItem={addItem} removeItem={removeItem} setPriceMode={setPriceMode}
                  moveGroup={moveGroup} moveItem={moveItem}
                />
              )}

              {tab === "terms" && (
                <TermsTab
                  groups={form.terms_groups || []} mode={mode}
                  setTermGroup={setTermGroup} setTermItem={setTermItem}
                  addTermGroup={addTermGroup} removeTermGroup={removeTermGroup}
                  addTermItem={addTermItem} removeTermItem={removeTermItem}
                  moveTermGroup={moveTermGroup}
                  templates={termTemplates}
                  onApplyTemplate={applyTermTemplate}
                  onSaveTemplate={saveTermTemplate}
                  onDeleteTemplate={deleteTermTemplate}
                />
              )}

              {tab === "pembayaran" && (
                <InvoicePaymentsPanel
                  grandTotal={data?.grand_total ?? totals.grand}
                  paid={ar.paid_amount || 0}
                  payments={ar.payments || []}
                  canEdit={canIssue}
                  locked={status === "draft" || status === "void"}
                  lockedReason={status === "void" ? "Invoice sudah dibatalkan — pembayaran tidak dapat dicatat." : "Terbitkan invoice terlebih dahulu untuk mulai mencatat pembayaran."}
                  onAdd={addArPayment}
                  onDelete={deleteArPayment}
                  onOpenAttachment={openPaymentProof}
                />
              )}

              {tab === "lampiran" && (
                <InvoiceDocs
                  attachments={docs}
                  invoices={[{ id: data?.id, number: data?.number, ppn_type: form.ppn_type }]}
                  fixedInvoiceId={data?.id}
                  canEdit={!!data?.id && status !== "void"}
                  canDelete={canIssue}
                  onUpload={uploadDocs}
                  onDelete={deleteDoc}
                  onOpen={openDoc}
                  testid="invoice-docs"
                />
              )}

              {/* FOOTER */}
              <div className="pt-4 border-t border-[#E5EEFA]/60 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {form.id && canIssue && status === "draft" && (
                    <IconAction icon={Trash2} label="Hapus Invoice" variant="danger" onClick={() => setConfirm("delete")} testid="invoice-delete-btn" />
                  )}
                  {form.id && canIssue && (status === "issued" || status === "paid" || status === "partial") && (
                    <IconAction icon={Ban} label="Batalkan Invoice" variant="danger" onClick={() => setConfirm("void")} testid="invoice-void-btn" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {form.id && (
                    <IconAction icon={MessageCircle} label="Kirim WhatsApp" variant="whatsapp" onClick={sendWhatsApp} testid="invoice-whatsapp-btn" />
                  )}
                  {form.id && (
                    <IconAction icon={FileDown} label="Unduh PDF" variant="success" onClick={downloadPdf} testid="invoice-download-btn" />
                  )}
                  {form.id && (
                    <IconAction icon={Printer} label="Cetak PDF" variant="info" onClick={printInvoicePdf} testid="invoice-print-btn" />
                  )}
                  <IconAction icon={X} label="Tutup" onClick={onClose} testid="invoice-modal-close" />
                </div>
              </div>
            </div>

            {/* LIVE PDF PREVIEW */}
            <div className="hidden lg:flex flex-1 min-w-0 border-l border-[#DCE0E7]/60 bg-[#E9EDF4]">
              <InvoicePdfPreview
                form={form} totals={totals} company={company}
                bankAccount={selectedBank}
                ownerName={selectedSigner?.name || team.find((u) => u.role === "owner")?.name}
                ownerJobTitle={selectedSigner?.title || team.find((u) => u.role === "owner")?.job_title}
                qrDataUri={data?.qr_data_uri || null}
                full={previewFull} onToggleFull={() => setPreviewFull((v) => !v)}
              />
            </div>
          </div>
        )}
      </DialogContent>

      <AlertDialog open={confirm !== null} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="rounded-3xl" data-testid="invoice-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "delete" ? "Hapus invoice ini?" : confirm === "issue" ? "Terbitkan invoice ini?" : "Batalkan invoice ini?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete" && <>Invoice <span className="font-mono font-semibold">{data?.number}</span> beserta seluruh itemnya akan dihapus permanen.</>}
              {confirm === "issue" && <>Setelah diterbitkan, invoice <span className="font-mono font-semibold">{data?.number}</span> menjadi dokumen resmi dan hanya Owner/Finance yang dapat mengubahnya.</>}
              {confirm === "void" && <>Invoice <span className="font-mono font-semibold">{data?.number}</span> akan ditandai batal (void) dan tidak lagi dianggap tagihan aktif.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invoice-confirm-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => (confirm === "delete" ? remove() : runAction(confirm))}
              data-testid="invoice-confirm-ok"
              className={confirm === "issue" ? "bg-[#3B4A63] hover:bg-[#2f3c52]" : "bg-[#CE6968] hover:bg-[#b95958]"}
            >
              {confirm === "delete" ? "Hapus" : confirm === "issue" ? "Terbitkan" : "Batalkan"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

/* ============ TERMIN ============ */
const TERMIN_PRESETS = [
  { label: "DP", percent: 50 },
  { label: "Progress", percent: 30 },
  { label: "Pelunasan", percent: null },
];

function BillingProgress({ projectBase, billedElsewhere, thisAmount, overBilled }) {
  const pctPrev = Math.min(100, (billedElsewhere / projectBase) * 100);
  const pctThis = Math.min(100 - pctPrev, (thisAmount / projectBase) * 100);
  const remaining = projectBase - billedElsewhere - thisAmount;
  return (
    <div data-testid="invoice-billing-progress">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">Progres Tagihan Proyek</span>
        <span className="text-[11px] font-num tabular-nums font-bold text-[#3B4A63]">
          {fmtIDR(billedElsewhere + thisAmount)} / {fmtIDR(projectBase)}
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-[#EDEFF3] overflow-hidden flex">
        <div className="h-full bg-[#B0C7EA] transition-all duration-500" style={{ width: `${pctPrev}%` }} />
        <div className={`h-full transition-all duration-500 ${overBilled ? "bg-[#CE6968]" : "bg-[#3B4A63]"}`} style={{ width: `${Math.max(0, pctThis)}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[11px]">
        <span className="text-[#A0A3A8]">Sudah ditagih sebelumnya {fmtIDR(billedElsewhere)}</span>
        <span className={remaining < -1 ? "text-[#CE6968] font-bold" : "text-[#626061] font-semibold"} data-testid="invoice-billing-remaining">
          {remaining < -1 ? `Lebih ${fmtIDR(Math.abs(remaining))}` : `Sisa ${fmtIDR(Math.max(0, remaining))}`}
        </span>
      </div>
      {overBilled && (
        <div className="mt-2 rounded-xl bg-[#FDE7E9] border border-[#FFB3BF] px-3 py-2 text-[11px] text-[#CE6968] font-semibold" data-testid="invoice-overbilled-warning">
          Total tagihan melebihi nilai proyek. Tetap bisa disimpan — pastikan ini memang pekerjaan tambahan.
        </div>
      )}
    </div>
  );
}

function TerminEditor({ form, setField, totals, billing, billedElsewhere, projectBase, totalAfterThis, overBilled }) {
  const on = !!form.termin_enabled;
  return (
    <div className="rounded-xl border border-[#DCE0E7] bg-white p-4 space-y-3" data-testid="invoice-termin-editor">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#3B4A63] font-bold">Tagihan Termin</div>
          <div className="text-[11px] text-[#A0A3A8] mt-0.5">
            Item tetap tampil harga penuh; yang ditagih hanya persentasenya.
          </div>
        </div>
        <Switch checked={on} onCheckedChange={(v) => setField("termin_enabled", v)} data-testid="inv-termin-toggle" />
      </div>

      {on && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            {TERMIN_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                data-testid={`inv-termin-preset-${p.label.toLowerCase()}`}
                onClick={() => {
                  setField("termin_label", p.label);
                  const sisa = projectBase > 0 ? Math.max(0, ((projectBase - billedElsewhere) / projectBase) * 100) : 100;
                  setField("termin_percent", p.percent ?? Math.round(sisa * 100) / 100);
                }}
                className={`h-7 px-3 rounded-full text-[11px] font-semibold border transition-all ${
                  form.termin_label === p.label ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-black/10 hover:border-[#3B4A63]"
                }`}
              >
                {p.label}{p.percent ? ` ${p.percent}%` : " (sisa)"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Label Termin">
              <input
                data-testid="inv-termin-label"
                value={form.termin_label}
                onChange={(e) => setField("termin_label", e.target.value)}
                className={inp}
                placeholder="DP / Progress / Pelunasan"
                maxLength={80}
              />
            </Field>
            <Field label="Persentase (%)">
              <NumberInput
                allowDecimal
                data-testid="inv-termin-percent"
                value={String(form.termin_percent ?? "").replace(".", ",")}
                onChange={(raw) => setField("termin_percent", raw.replace(",", "."))}
                className="h-10 rounded-lg bg-white font-num"
                placeholder="50"
              />
            </Field>
            <Field label="Nilai Dasar (DPP)">
              <NumberInput
                data-testid="inv-termin-base"
                value={form.termin_base}
                onChange={(raw) => setField("termin_base", raw)}
                className="h-10 rounded-lg bg-white font-num"
                placeholder="Nilai proyek"
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-[#F5F9FF] border border-[#B0C7EA]/60 px-3 py-2">
            <span className="text-[11px] text-[#626061] font-num tabular-nums">
              {num(form.termin_percent)}% × {fmtIDR(form.termin_base)}
            </span>
            <span className="text-sm font-bold text-[#3B4A63] font-num tabular-nums" data-testid="inv-termin-computed">{fmtIDR(totals.billed)}</span>
          </div>

          <button
            type="button"
            data-testid="inv-termin-use-item-total"
            onClick={() => setField("termin_base", totals.subtotal)}
            className="text-[11px] font-semibold text-[#3B4A63] hover:underline"
          >
            Pakai nilai item invoice ini ({fmtIDR(totals.subtotal)}) sebagai nilai dasar
          </button>
        </>
      )}

      {billing && projectBase > 0 && (
        <div className="pt-2 border-t border-[#DCE0E7]">
          <BillingProgress
            projectBase={projectBase}
            billedElsewhere={billedElsewhere}
            thisAmount={on ? totals.billed : totals.subtotal}
            overBilled={overBilled}
          />
        </div>
      )}
    </div>
  );
}

/* ============ PDF-IDENTICAL A4 LIVE PREVIEW ============ */
const kidLines = (it) => (it.children || []).filter((c) => (c || "").trim());
const KidRows = ({ item }) => (
  <>
    {kidLines(item).map((c, ci) => (
      <div key={ci} style={{ display: "flex", gap: 4, marginTop: 1.5, paddingLeft: 14 }}>
        <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.45, flexShrink: 0 }}>&middot;</span>
        <span style={{ color: "#9AA0AC", fontSize: 9, lineHeight: 1.45 }}>{c}</span>
      </div>
    ))}
  </>
);

const A4_W = 794;
const A4_H = 1123;
const PAGE_PAD = 36;
const NAVY = "#3B4A63";

const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dMY = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return String(iso);
  return `${d} ${MONTHS_EN[Number(m) - 1] || m} ${y}`;
};
const fmtQty = (v) => num(v).toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",");

const sMetaLbl = { fontSize: 10, color: "#8A8F98", padding: "1.5px 0", width: 100 };
const sMetaVal = { fontSize: 10, fontWeight: 700, padding: "1.5px 0" };
const sBox = { background: "#F4F6FA", padding: "13px 15px", verticalAlign: "top", width: "50%", borderRadius: 10 };
const sBoxTtl = { display: "inline-block", background: "#fff", borderRadius: 20, padding: "5px 12px 4px", fontSize: 8, lineHeight: 1, letterSpacing: 1.6, color: NAVY, fontWeight: 700, marginBottom: 9 };
const sKvK = { fontSize: 10, color: "#8A8F98", padding: "2px 14px 2px 0", verticalAlign: "top", width: 78, whiteSpace: "nowrap" };
const sKvV = { fontSize: 10, padding: "2px 0", verticalAlign: "top" };
const sTh = { background: NAVY, color: "#fff", fontSize: 8.5, letterSpacing: 1.2, padding: 10, textAlign: "left" };
const sTd = { padding: "9px 10px", fontSize: 10.5, borderBottom: "1px solid #E7EAF0", verticalAlign: "top" };
const sTdR = { ...sTd, textAlign: "right", whiteSpace: "nowrap" };
const sTotLbl = { fontSize: 10.5, color: "#626873", padding: "4px 0" };
const sTotVal = { fontSize: 10.5, fontWeight: 700, padding: "4px 0", textAlign: "right", whiteSpace: "nowrap" };

export function InvoicePdfPreview({
  form, totals, company, bankAccount, ownerName, ownerJobTitle, qrDataUri, full, onToggleFull,
  docTitle = "Invoice", docKind = "INVOICE DOCUMENT", byLabel = "BILLED BY", toLabel = "BILLED TO",
  party = null, hideStamp = false, bankTitle = "Payment Transfer To", variant = "invoice",
  totalLabel = "Total Due",
}) {
  const isPo = variant === "po";
  const toParty = party || { role: "Customer", name: form.customer, address: form.customer_address, contact: form.pic_contact };
  const wrapRef = useRef(null);
  const sheetRef = useRef(null);
  const [zoom, setZoom] = useState(0.8);
  const [scale, setScale] = useState(0.8);
  const [wrapW, setWrapW] = useState(0);
  const [sheetH, setSheetH] = useState(A4_H);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const apply = () => {
      const avail = el.clientWidth - 32;
      setWrapW(el.clientWidth);
      setScale(zoom === "fit" ? Math.max(0.2, Math.min(1, avail / A4_W)) : zoom);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [zoom, full]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSheetH(Math.max(A4_H, el.offsetHeight)));
    ro.observe(el);
    return () => ro.disconnect();
  });

  const step = (d) => setZoom((z) => {
    const cur = z === "fit" ? scale : z;
    return Math.max(0.3, Math.min(1.4, Math.round((cur + d) * 20) / 20));
  });

  const companyName = company?.name || "Sales Pipeline";
  const groups = form.groups || [];
  const termGroups = form.terms_groups || [];
  const bank = bankAccount
    ? { name: bankAccount.bank_name, account: bankAccount.account_no, holder: bankAccount.holder }
    : isPo ? { name: "", account: "", holder: "" }
    : (form.ppn_type === "ppn"
      ? { name: company?.bank_ppn_name, account: company?.bank_ppn_account, holder: company?.bank_ppn_holder }
      : { name: company?.bank_nonppn_name, account: company?.bank_nonppn_account, holder: company?.bank_nonppn_holder });
  const companyContact = company?.phone || company?.email || "-";
  const ownerNameSafe = ownerName || "-";
  const ownerJobTitleSafe = ownerJobTitle || "Owner";

  const volCell = (qty, unit, bg) => (
    <td style={{ ...sTd, background: bg, padding: "9px 4px" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody><tr>
        <td style={{ width: "50%", textAlign: "right", fontSize: 10.5, padding: 0, whiteSpace: "nowrap" }}>{fmtQty(qty)}</td>
        <td style={{ width: "50%", textAlign: "left", fontSize: 10.5, padding: "0 0 0 4px", whiteSpace: "nowrap" }}>{unit}</td>
      </tr></tbody></table>
    </td>
  );

  const rows = [];
  let alt = false;
  groups.forEach((g, gi) => {
    const gsub = totals.gsubs[gi] || 0;
    const bg = alt ? "#F7F8FB" : "transparent";
    alt = !alt;
    const cell = (extra = {}) => ({ ...sTd, background: bg, ...extra });

    if (g.price_mode !== "itemized") {
      const subs = (g.items || []).filter((it) => (it.description || "").trim() || kidLines(it).length > 0);
      rows.push(
        <tr key={`g${gi}`}>
          <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
          <td style={cell()}>
            <b style={{ fontSize: 11 }}>{g.title}</b>
            {subs.length > 0 && (
              <div style={{ marginTop: 2 }}>
                {subs.map((it, ii) => (
                  <div key={ii}>
                    {(it.description || "").trim() && (
                      <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                        <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5, flexShrink: 0 }}>&ndash;</span>
                        <span style={{ color: "#7A8090", fontSize: 9.5, lineHeight: 1.5 }}>{it.description}</span>
                      </div>
                    )}
                    <KidRows item={it} />
                  </div>
                ))}
              </div>
            )}
          </td>
          {volCell(g.qty, g.unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(g.price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(gsub)}</td>
        </tr>
      );
      return;
    }

    const items = g.items || [];
    const single = items.length === 1 && !(items[0].description || "").trim();
    if (single) {
      rows.push(
        <tr key={`g${gi}`}>
          <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
          <td style={cell()}><b style={{ fontSize: 11 }}>{g.title}</b><KidRows item={items[0]} /></td>
          {volCell(items[0].qty, items[0].unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(items[0].price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(items[0].qty) * num(items[0].price))}</td>
        </tr>
      );
      return;
    }

    rows.push(
      <tr key={`g${gi}`}>
        <td style={{ ...cell(), textAlign: "center" }}>{gi + 1}</td>
        <td style={cell()} colSpan={4}><b style={{ fontSize: 11 }}>{g.title}</b></td>
      </tr>
    );
    items.forEach((it, ii) => {
      rows.push(
        <tr key={`g${gi}i${ii}`}>
          <td style={cell()}></td>
          <td style={cell()}>{it.description}<KidRows item={it} /></td>
          {volCell(it.qty, it.unit, bg)}
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(it.price)}</td>
          <td style={{ ...sTdR, background: bg }}>{fmtIDR(num(it.qty) * num(it.price))}</td>
        </tr>
      );
    });
    rows.push(
      <tr key={`g${gi}sub`}>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5, color: "#4A5160" }} colSpan={4}>Subtotal ({gi + 1})</td>
        <td style={{ ...sTdR, background: "#EFF2F8", fontSize: 9.5 }}><b>{fmtIDR(gsub)}</b></td>
      </tr>
    );
  });

  return (
    <div className="flex flex-col w-full min-w-0" data-testid="invoice-live-preview">
      <div className="flex items-center justify-between gap-2 pl-4 pr-14 h-12 shrink-0 border-b border-[#DCE0E7] bg-white/70 backdrop-blur">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B8FE2] font-bold">Preview PDF · A4</div>
        <div className="flex items-center gap-1">
          <button onClick={() => step(-0.1)} data-testid="inv-preview-zoom-out" title="Perkecil" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomOut className="w-4 h-4" /></button>
          <span className="text-[11px] font-bold text-[#3B4A63] font-num tabular-nums w-11 text-center" data-testid="inv-preview-zoom-label">{Math.round(scale * 100)}%</span>
          <button onClick={() => step(0.1)} data-testid="inv-preview-zoom-in" title="Perbesar" className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => setZoom("fit")} data-testid="inv-preview-zoom-fit" title="Sesuaikan lebar" className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${zoom === "fit" ? "bg-[#3B4A63] text-white" : "hover:bg-[#E5EEFA] text-[#3B4A63]"}`}><Scan className="w-4 h-4" /></button>
          <div className="w-px h-5 bg-[#DCE0E7] mx-1" />
          <button onClick={onToggleFull} data-testid="inv-preview-toggle-full" title={full ? "Tampilkan form" : "Lebar penuh"} className="w-8 h-8 rounded-full hover:bg-[#E5EEFA] text-[#3B4A63] inline-flex items-center justify-center">
            {full ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div ref={wrapRef} className="flex-1 overflow-auto p-4">
        <div style={{ width: A4_W * scale, height: sheetH * scale }} className={A4_W * scale <= wrapW - 32 ? "mx-auto" : ""}>
          <div
            ref={sheetRef}
            data-testid="invoice-preview-sheet"
            style={{
              width: A4_W, minHeight: A4_H, padding: PAGE_PAD, background: "#fff",
              transform: `scale(${scale})`, transformOrigin: "top left",
              fontFamily: "'DM Sans', Arial, Helvetica, sans-serif", color: "#14161F", fontSize: 10.5, lineHeight: 1.4,
              boxShadow: "0 12px 40px -12px rgba(20,22,31,0.35)",
              display: "flex", flexDirection: "column",
            }}
          >
            {/* Header */}
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <tbody><tr>
                <td style={{ verticalAlign: "top", width: "50%" }}>
                  <h1 style={{ fontSize: 26, fontWeight: 700, color: NAVY, margin: "0 0 12px", letterSpacing: 0.4 }}>{docTitle}</h1>
                  <table style={{ borderCollapse: "collapse" }}>
                    <tbody>
                      <tr><td style={sMetaLbl}>{docTitle} No.</td><td style={sMetaVal}>{form.number || "—"}</td></tr>
                      <tr><td style={sMetaLbl}>{docTitle} Date</td><td style={sMetaVal}>{dMY(form.date)}</td></tr>
                      <tr><td style={sMetaLbl}>Due Date</td><td style={sMetaVal}>{dMY(form.due_date)}</td></tr>
                    </tbody>
                  </table>
                  {!hideStamp && (form.status === "draft" || form.status === "paid") && (
                    <div style={{
                      display: "inline-block", marginTop: 8, fontSize: 12, fontWeight: 700, letterSpacing: 3,
                      padding: "5px 14px", borderRadius: 6,
                      color: form.status === "paid" ? "#1F8F52" : "#8A8F98",
                      border: `2px solid ${form.status === "paid" ? "#57D28A" : "#C7CCD6"}`,
                    }}>{form.status === "paid" ? "PAID" : "DRAFT"}</div>
                  )}
                </td>
                <td style={{ verticalAlign: "bottom", width: "26%", paddingLeft: 20 }}>
                  {form.quotation_number && (
                    <>
                      <div style={{ fontSize: 10, color: "#8A8F98", whiteSpace: "nowrap" }}>Ref. Quotation</div>
                      <div style={{ fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", marginTop: 2 }}>{form.quotation_number}</div>
                    </>
                  )}
                </td>
                <td style={{ verticalAlign: "top", textAlign: "right", width: "24%" }}>
                  {company?.logo
                    ? <img src={company.logo} alt="logo" style={{ maxHeight: 74, maxWidth: 270, borderRadius: 10, display: "inline-block", marginLeft: "auto" }} />
                    : (
                      <>
                        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.6 }}>{companyName}</div>
                        <div style={{ fontSize: 8, color: "#8A8F98", letterSpacing: 2.4, marginTop: 3 }}>{docKind}</div>
                      </>
                    )}
                </td>
              </tr></tbody>
            </table>

            {/* Info boxes */}
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "14px 0", marginTop: 18, marginLeft: -14, marginRight: -14 }}>
              <tbody><tr>
                <td style={sBox}>
                  <div style={sBoxTtl}>{byLabel}</div>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <tr><td style={sKvK}>Company</td><td style={sKvV}><b>{companyName}</b></td></tr>
                    <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{company?.address || "-"}</td></tr>
                    <tr><td style={sKvK}>Contact</td><td style={sKvV}>{companyContact}</td></tr>
                  </tbody></table>
                </td>
                <td style={sBox}>
                  <div style={sBoxTtl}>{toLabel}</div>
                  <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                    <tr><td style={sKvK}>{toParty.role}</td><td style={sKvV}><b>{toParty.name || "-"}</b></td></tr>
                    <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{toParty.address || "-"}</td></tr>
                    <tr><td style={sKvK}>Contact</td><td style={sKvV}>{toParty.contact || "-"}</td></tr>
                  </tbody></table>
                </td>
              </tr></tbody>
            </table>

            {/* Items */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20 }}>
              <thead><tr>
                <th style={{ ...sTh, width: 26, textAlign: "center" }}>#</th>
                <th style={sTh}>ITEM DESCRIPTION</th>
                <th style={{ ...sTh, width: 78, textAlign: "center" }}>VOLUME</th>
                <th style={{ ...sTh, width: 104, textAlign: "right" }}>RATE</th>
                <th style={{ ...sTh, width: 110, textAlign: "right" }}>AMOUNT</th>
              </tr></thead>
              <tbody>{rows}</tbody>
            </table>

            {/* Terms + bank + totals */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 22 }}>
              <tbody><tr>
                <td style={{ width: "56%", verticalAlign: "top", paddingRight: 26 }}>
                  {termGroups.length > 0 && (
                    <>
                      <h3 style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, margin: "0 0 7px", letterSpacing: 0.3 }}>Terms and Conditions</h3>
                      {termGroups.map((g, gi) => (
                        <Fragment key={gi}>
                          <div style={{ fontSize: 10, lineHeight: 1.5, marginBottom: 2, display: "flex", gap: 0 }}>
                            <span style={{ width: 16, flexShrink: 0, color: NAVY, fontWeight: 700 }}>{gi + 1}.</span>
                            <span>{g.title}</span>
                          </div>
                          {(g.items || []).map((it, ii) => (
                            <div key={ii} style={{ display: "flex", gap: 4, paddingLeft: 16 }}>
                              <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.5, flexShrink: 0 }}>&ndash;</span>
                              <span style={{ color: "#5B616E", fontSize: 9.5, lineHeight: 1.5 }}>{it}</span>
                            </div>
                          ))}
                        </Fragment>
                      ))}
                    </>
                  )}
                  <div style={{ display: (bank.name || bank.account) ? "block" : "none", marginTop: termGroups.length > 0 ? 24 : 0, padding: "12px 14px", background: "#F4F6FA", borderLeft: `3px solid ${NAVY}`, borderRadius: "0 10px 10px 0" }} data-testid="invoice-preview-bank">
                    <h3 style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, margin: "0 0 6px", letterSpacing: 0.3 }}>{bankTitle}</h3>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                      <tr><td style={sKvK}>Bank</td><td style={sKvV}><b>{bank.name || "-"}</b></td></tr>
                      <tr><td style={sKvK}>Account No.</td><td style={sKvV}><b>{bank.account || "-"}</b></td></tr>
                      <tr><td style={sKvK}>Account Name</td><td style={sKvV}>{bank.holder || "-"}</td></tr>
                    </tbody></table>
                  </div>
                </td>
                <td style={{ width: "44%", verticalAlign: "top" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
                    <tr><td style={sTotLbl}>Sub Total</td><td style={sTotVal}>{fmtIDR(totals.subtotal)}</td></tr>
                    {form.termin_enabled && Math.abs(num(form.termin_base) - totals.subtotal) > 0.01 && (
                      <tr><td style={sTotLbl}>Nilai Dasar Termin</td><td style={sTotVal}>{fmtIDR(form.termin_base)}</td></tr>
                    )}
                    {form.termin_enabled && (
                      <tr data-testid="invoice-preview-termin-row">
                        <td style={{ ...sTotLbl, fontWeight: 700, color: NAVY }}>
                          {(form.termin_label || "Termin") + " (" + num(form.termin_percent) + "%)"}
                        </td>
                        <td style={sTotVal}>{fmtIDR(totals.billed)}</td>
                      </tr>
                    )}
                    {form.ppn_type === "ppn" && (
                      <tr><td style={sTotLbl}>VAT 11%</td><td style={sTotVal}>{fmtIDR(totals.ppn)}</td></tr>
                    )}
                    <tr>
                      <td style={{ fontSize: 14, fontWeight: 700, color: NAVY, borderTop: "1px solid #DDE2EC", paddingTop: 9 }}>{totalLabel}</td>
                      <td style={{ fontSize: 16, fontWeight: 700, color: NAVY, textAlign: "right", whiteSpace: "nowrap", borderTop: "1px solid #DDE2EC", paddingTop: 9 }}>{fmtIDR(totals.grand)}</td>
                    </tr>
                  </tbody></table>
                  <div style={{ fontSize: 8.5, color: "#8A8F98", letterSpacing: 1.2, marginTop: 12 }}>TOTAL (IN WORDS)</div>
                  <div style={{ fontSize: 10, fontWeight: 700, lineHeight: 1.45, marginTop: 3 }}>{terbilang(totals.grand)} Rupiah</div>
                </td>
              </tr>
              <tr>
                <td style={{ width: "56%", verticalAlign: "bottom", paddingRight: 26 }}>
                  <div style={{ display: isPo ? "none" : "block", marginTop: 14, padding: "12px 14px", background: "#F4F6FA", borderLeft: `3px solid ${NAVY}`, borderRadius: "0 10px 10px 0" }} data-testid="invoice-preview-npwp">
                    <h3 style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, margin: "0 0 6px", letterSpacing: 0.3 }}>NPWP</h3>
                    <table style={{ borderCollapse: "collapse", width: "100%" }}><tbody>
                      {form.ppn_type === "ppn" ? (
                        <>
                          <tr><td style={sKvK}>Company Name</td><td style={sKvV}><b>{company?.npwp_ppn_name || "-"}</b></td></tr>
                          <tr><td style={sKvK}>Number</td><td style={sKvV}><b>{company?.npwp_ppn_number || "-"}</b></td></tr>
                          <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{company?.npwp_ppn_address || "-"}</td></tr>
                        </>
                      ) : (
                        <>
                          <tr><td style={sKvK}>Number</td><td style={sKvV}><b>{company?.npwp_nonppn_number || "-"}</b></td></tr>
                          <tr><td style={sKvK}>Name</td><td style={sKvV}><b>{company?.npwp_nonppn_name || "-"}</b></td></tr>
                          <tr><td style={sKvK}>NIK</td><td style={sKvV}>{company?.npwp_nonppn_nik || "-"}</td></tr>
                          <tr><td style={sKvK}>Address</td><td style={{ ...sKvV, whiteSpace: "pre-wrap" }}>{company?.npwp_nonppn_address || "-"}</td></tr>
                        </>
                      )}
                    </tbody></table>
                  </div>
                </td>
                <td style={{ width: "44%", verticalAlign: "bottom" }}>
                  <div style={{ display: "flex", alignItems: "flex-end" }} data-testid="invoice-preview-signature">
                    <div style={{ width: 124, flexShrink: 0, paddingRight: 14 }}>
                      <div style={{ width: 110, height: 80, border: "1px dashed #C7CCD6", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                        <span style={{ fontSize: 6.5, color: "#A0A3A8", letterSpacing: 0.6, lineHeight: 1.4 }}>{isPo ? "TANDA TANGAN" : <>MATERAI<br />&amp; TTD</>}</span>
                      </div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#14161F", marginTop: 6 }}>{ownerNameSafe}</div>
                      <div style={{ fontSize: 8.5, color: "#8A8F98", marginTop: 2 }}>{ownerJobTitleSafe}</div>
                    </div>
                    <div style={{ width: 82, flexShrink: 0, display: isPo ? "none" : "block" }} data-testid="invoice-preview-qr">
                      {qrDataUri
                        ? <img src={qrDataUri} alt="QR" style={{ width: 82, height: 82, display: "block" }} />
                        : <div style={{ width: 82, height: 82, border: "1px dashed #C7CCD6" }} />}
                      {qrDataUri && (
                        <div style={{ fontSize: 7.5, color: "#8A8F98", letterSpacing: 0.8, marginTop: 6, lineHeight: 1.35 }}>SCAN OR CLICK QR TO VERIFY</div>
                      )}
                    </div>
                  </div>
                </td>
              </tr></tbody>
            </table>

            {/* Footer */}
            <div style={{ marginTop: "auto", paddingTop: 20 }}>
              <div style={{ borderTop: "1px solid #E7EAF0", paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#3B4A63", letterSpacing: 0.3 }}>Your Creative-Tech Partner</div>
                  <div style={{ fontSize: 7, fontWeight: 400, color: "#A0A3A8", marginTop: 2 }}>Generated by IPTEC&reg; Supersales App.</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 8, color: "#3B4A63", fontWeight: 700, letterSpacing: 0.3 }}>{company?.web || "www.iptec.id"}</div>
                  <div style={{ fontSize: 7, fontWeight: 400, color: "#A0A3A8", marginTop: 2 }}>Page 1 of 1</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
